/**
 * The install invitation on Perfil → Seguridad — the same card as the dashboard's, and
 * the permanent home of the gesture.
 *
 * Founder decision, 2026-08-28. Before it, this slot held a plain steps panel and the
 * account foot below held an "Instalar la app" row whose only effect was to scroll back
 * up to that panel: two things doing one job, and the lower one indistinguishable from a
 * dead button. The row is gone and the card took the panel's place, chosen as the newer
 * design.
 *
 * The one difference from the dashboard, and it is deliberate: **no dismissal**. The
 * dashboard's card retires itself after two "ahora no" (R-T393-10); if this one did the
 * same, a member who declined twice would have no route to the instructions at all. A
 * settings screen is where a thing lives, not where it asks.
 */
import { useTranslation } from "react-i18next";
import { InstallInviteBody } from "./InstallInviteBody";
import { useInstallPrompt } from "./useInstallPrompt";
import type { InstallPlatform } from "~/lib/pwa/device";
import { productDataRitual, type ProductDataRitual } from "~/lib/product-visual-line";

interface InstallInvitePanelProps {
  /** Resolved server-side from the User-Agent, so the first paint is already correct. */
  platform: InstallPlatform;
  /**
   * The product id of a ritual she owns, or `null`. Only used to pick the card's line —
   * no query is made for it; Perfil's loader already carries `ritualGoals`.
   */
  ritualProductId?: string | null;
}

export function InstallInvitePanel({ platform, ritualProductId }: InstallInvitePanelProps) {
  const { t } = useTranslation("pwa");
  const { deferredPrompt, promptInstall, installing } = useInstallPrompt();

  /**
   * The card's material is the ritual's, and `[data-ritual]` is what declares it. The
   * dashboard gets it from the shell; Perfil has no ritual context of its own, and
   * without one BOTH `--ritual-accent-light` and `--ritual-hero-grad` are undefined —
   * measured result: a flat black card with a white eyebrow indistinguishable from the
   * title, the loudest possible version of itself between two soft white panels.
   *
   * `addon` when she owns no ritual: it is the line the system reserves for something
   * that belongs to no ritual in particular, which is exactly what a device gesture is.
   * Same guard `ritualDataAttrFromDashboard` applies for the same reason.
   */
  const ritual: ProductDataRitual = ritualProductId
    ? productDataRitual({ productId: ritualProductId, productType: "ritual" })
    : "addon";

  return (
    // NOT `.card-install`: that is the dashboard's twelve-track grid slot and means
    // nothing here, and `bentoCardMotion` belongs to the grid's stagger. The card carries
    // its own background, padding and radius, so it stands alone.
    //
    // `.pf-stack-block` is what joins it to the tab's vertical rhythm. Without it the
    // adjacency rule (app.css) sees no `.pf-panel` on either side and the card renders
    // flush against Seguridad above and "Más adelante" below — measured at 0px both ways,
    // which is the same defect the founder caught the first time these panels touched.
    <article
      className="ii-card pf-stack-block"
      data-ritual={ritual}
      role="region"
      aria-label={t("invite_aria_label")}
    >
      <InstallInviteBody
        platform={platform}
        deferredPrompt={deferredPrompt}
        installing={installing}
        onInstall={() => void promptInstall()}
      />
    </article>
  );
}
